import useFocusTrap from '@charlietango/use-focus-trap';
import { ReactNode, useRef } from 'react';
import { useTheme } from 'styled-components';

import { OneError } from '#/errors/oneError';
import { filterSentryLogs, logErrorToSentry } from '#/errors/sentry';
import { useDataContext } from '#/hooks/useDataContext';
import { AppStatus, ButtonVariant } from '#/service/constants';
import { tb, useTranslationStore } from '#/store/translation/translation.store';

import { useErrorTranslations } from './Status.hook';
import {
  BackContainer,
  BackImage,
  DeveloperContainer,
  DeveloperSubText,
  DeveloperText,
  Dialog,
  DialogButton,
  DialogButtonContainer,
  OverlayDiv,
  StatusIcon,
  StatusMessage,
  StatusTitle,
} from './Status.styled';

export interface StatusProps {
  /**
   * @optional Background image URL.
   * If not provided, the default background color based on `displayAs` will be used
   * */
  backgroundImageUrl?: string;

  /**
   * @default true Applies a statically defined linear gradient on the background image
   * Disable this if you want to use background image with a gradient already applied
   * It does make sense to use it only with `backgroundImageUrl` provided
   * */
  applyDefaultGradient?: boolean;

  /** @default false */
  fatalError?: boolean;

  /** @default AppStatus.Active Application status */
  status?: AppStatus;

  /** Status title text. If left empty a translation dependent on the chosen `status` will be used */
  statusTitle?: string;

  /** Status text message. If left empty a translation dependent on the chosen `status` will be used */
  statusMessage?: string;

  /** Text displayed on the primary dismiss button */
  dismissMessage?: string;

  /** Error instance */
  error?: Error | OneError;

  /** Callback function for dismissing the component.
   * Although it's called dismiss, it's usually not cancel, but something that fixes the error.
   * For example the login button on the login required status.
   */
  onDismiss?: () => void;

  /** Callback function for the secondary cancel action.
   * This is optional and only used when there are two actions available to the user.
   * For example, on a logout required status, you might have a "Logout" (onDismiss) and "Cancel" (onCancel) buttons.
   * Uses the standard translation for "Cancel".
   */
  onCancel?: () => void;

  /** Determines how your Status message will be shown.
   * `page` makes it in an opaque full-page container.
   * `dialog` makes it in a slightly transparent container.
   * This is to align with the Colors-keys guide in a non-breaking way
   * @default 'page'
   **/
  displayAs?: 'page' | 'dialog';

  /**
   * Custom contents of the Status component. It will be displayed below the Status title.
   * If you use children prop, then `statusMessage` and dismiss props will be ignored.
   */
  children?: ReactNode;

  /**
   * Custom title id for accessibility.
   */
  errorTitleId?: string;

  /**
   * Custom error message id for accessibility.
   */
  errorMessageId?: string;

  /**
   * The component stack trace at the time of the error
   */
  componentStack?: string;
}

/** Dialog to display information about the application status. */
export function Status(props: Readonly<StatusProps>) {
  const {
    fatalError = false,
    status = AppStatus.Active,
    statusTitle = '',
    statusMessage = '',
    dismissMessage = '',
    error,
    onDismiss,
    onCancel,
    backgroundImageUrl,
    applyDefaultGradient = true,
    displayAs = 'page',
    children,
    errorTitleId = 'status-title',
    errorMessageId = 'status-message',
    componentStack,
  } = props;

  const { getOneErrorContext } = useDataContext();

  const theme = useTheme();
  const ref = useFocusTrap();
  const hasSentLogs = useRef(false);

  // just to make sure we re-render when translations change.
  // ex: we render app-wide geo-blocked status before the translations were initialised.
  useTranslationStore();

  const {
    imageOverlayBackground = '',
    pageBackground,
    dialogHeadingForeground = '#FFF',
    dialogParagraphForeground = '#FFF',
    pageHeadingForeground,
    pageParagraphForeground,
  } = theme.color;

  // use page or dialog colors depending on `displayAs`
  const [backgroundColor, headingColor, paragraphColor] =
    displayAs === 'page'
      ? [pageBackground, pageHeadingForeground, pageParagraphForeground]
      : [
          imageOverlayBackground,
          dialogHeadingForeground,
          dialogParagraphForeground,
        ];

  const { sendSentryLogs, code, details } = filterSentryLogs(error, status);
  const context = getOneErrorContext(code, details);

  const { isActiveAndNotFatal, dismiss, message, statusIcon, subtext, title } =
    useErrorTranslations({
      error,
      fatalError,
      status,
      statusTitle,
      statusMessage,
      dismissMessage,
      context,
    });

  if (sendSentryLogs && !hasSentLogs.current && !isActiveAndNotFatal) {
    logErrorToSentry(error, context, message, componentStack);
    hasSentLogs.current = true;
  }

  if (isActiveAndNotFatal) {
    return null;
  }

  return (
    <OverlayDiv
      data-testid="status-overlay"
      {...{ backgroundColor, color: paragraphColor }}
      ref={ref}
    >
      {backgroundImageUrl && (
        <BackContainer>
          <BackImage
            data-apply-gradient={applyDefaultGradient}
            src={backgroundImageUrl}
          />
        </BackContainer>
      )}
      <Dialog
        aria-describedby={errorMessageId}
        aria-labelledby={errorTitleId}
        aria-live="polite"
        aria-modal={true}
        data-testid="status"
        role="alertdialog"
      >
        {statusIcon && (
          <StatusIcon
            aria-hidden="true"
            color={headingColor}
            name={statusIcon}
          />
        )}
        <StatusTitle color={headingColor} id={errorTitleId}>
          {title}
        </StatusTitle>
        {children ?? (
          <>
            <StatusMessage data-testid="message" id={errorMessageId} lines={5}>
              {message}
            </StatusMessage>
            <DialogButtonContainer>
              {onDismiss && (
                <DialogButton data-testid="dismiss" onClick={onDismiss}>
                  {dismiss}
                </DialogButton>
              )}
              {onCancel && (
                <DialogButton
                  data-testid="cancel"
                  onClick={onCancel}
                  variant={ButtonVariant.Secondary}
                >
                  {tb('button.cancel')}
                </DialogButton>
              )}
            </DialogButtonContainer>
          </>
        )}
      </Dialog>
      {subtext ? (
        <DeveloperContainer>
          <DeveloperText data-testid="devText">
            {tb('errors.developer.title')}:
          </DeveloperText>
          <DeveloperSubText data-testid="devSubText" lines={4}>
            {subtext}
          </DeveloperSubText>
        </DeveloperContainer>
      ) : null}
    </OverlayDiv>
  );
}
